import { useLocation, useNavigate } from "react-router-dom";
import { Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/lib/theme/ThemeProvider";
import { useSaveAppearance } from "@/components/appearance/AppearanceActions";

// Shown on every page while an unsaved theme preview is active and the panel is closed.
export default function PreviewBar() {
  const th = useTheme();
  const navigate = useNavigate();
  const { save, busy, blocked } = useSaveAppearance();
  const { pathname } = useLocation();
  if (!th.draft || !th.dirty || th.panelOpen) return null;
  const clinic = th.draft.kind === "clinic";
  // These pages show their own Save / Cancel controls.
  if (pathname === "/appearance" || (clinic && pathname === "/settings")) return null;
  return (
    <div
      role="status"
      className="fixed bottom-4 left-1/2 z-[55] flex max-w-[calc(100vw-1rem)] -translate-x-1/2 items-center gap-1.5 sm:gap-2 whitespace-nowrap rounded-full border border-le-border bg-le-surface/95 py-1.5 pl-3 pr-1.5 sm:py-2 sm:pl-4 sm:pr-2 shadow-lg backdrop-blur-md"
      data-testid="theme-preview-bar"
    >
      <Palette className="h-4 w-4 text-le-primary-text" aria-hidden />
      <span className="text-sm font-medium text-le-text">
        <span className="hidden sm:inline">{clinic ? "Previewing a clinic theme draft — only you can see it" : "Previewing an unsaved theme"}</span>
        <span className="sm:hidden">{clinic ? "Clinic draft preview" : "Unsaved theme"}</span>
      </span>
      {clinic ? (
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate("/settings?tab=appearance")} data-testid="preview-bar-clinic-settings">
          Back to clinic settings
        </Button>
      ) : (
        <>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => th.setPanelOpen(true)} data-testid="preview-bar-edit">
            <span className="sm:hidden">Edit</span><span className="hidden sm:inline">Keep editing</span>
          </Button>
          <Button size="sm" className="rounded-full" onClick={save} disabled={busy || blocked} data-testid="preview-bar-save">
            Save
          </Button>
        </>
      )}
      <Button size="sm" variant="ghost" className="rounded-full" onClick={th.cancelPreview} data-testid="preview-bar-cancel">
        Cancel
      </Button>
    </div>
  );
}
